import React, { useCallback, useContext, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Box,
  Paper,
  Tooltip,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { format } from 'date-fns';

import { cacheKeys, routes } from 'config';
import { DialogContext, LayoutContext, LocalizationContext } from 'contexts';
import { ReportType } from 'types/Report';
import { getReportStatusLabels } from 'helpers/report';
import { reportsClient } from 'clients/reports/reportsClient';
import { ReportStatus } from 'clients/reports/reportsClient.types';
import { useDebouncedQueryState } from 'shared/hooks/useDebouncedQueryState';
import { useQueryState } from 'shared/hooks/useQueryState';
import { useReports } from 'shared/hooks/useReports';
import { BodyLoading } from 'shared/components/layout/BodyLoading/BodyLoading';


export const ReportList = () => {

  const navigate = useNavigate();
  const { dictionary } = useContext(LocalizationContext);
  const { genericError, genericFeedback, isMobile } = useContext(LayoutContext);
  const queryClient = useQueryClient();
  const { asyncConfirmation } = useContext(DialogContext);
  const [, currSearch] = useDebouncedQueryState('search');
  const [gteDate] = useQueryState('gteDate');
  const [lteDate] = useQueryState('lteDate');
  const [reporter] = useQueryState('reporter');
  const [reportBeingDeleted, setReportBeingDeleted] = useState<number>();

  const { reports, status } = useReports({
    search: currSearch,
    start_date__gte: gteDate,
    start_date__lte: lteDate,
    created_by: reporter,
    category: ReportType.Report,
  });

  const onDelete = useCallback(async (reportId: number) => {
    setReportBeingDeleted(reportId);
    
    try {
      const userConfirmed = await asyncConfirmation({
        title: dictionary.reports.list.deleteConfirmation,
      });

      if(userConfirmed) {
        await reportsClient.deleteReport(reportId);
        await queryClient.invalidateQueries(cacheKeys.reports.getReports);
        await queryClient.invalidateQueries(cacheKeys.tasks.getDropdownResults);
        await queryClient.invalidateQueries(cacheKeys.dashboard.getDashboardMetadata);
        genericFeedback(dictionary.reports.form.reportDeleted);
      }
    } catch(e) {
      genericError();
      console.error(e);
    }
    
    setReportBeingDeleted(undefined);
  }, [dictionary, queryClient, asyncConfirmation, genericError, genericFeedback]);

  return (
    <>
      {(status === 'loading' || !reports) && (
        <BodyLoading height="40vh"/>
      )}
      {reports?.map(({ id, title, status, created_by, start_date, updated_at, updated_by, meta }: any, i: number) => (
        <Grid item xs={12} key={i}>
          <Box
            p={4}
            component={Paper}
            sx={{
              overflow: 'hidden',
              position: 'relative',
              height: '100%',
              cursor: 'pointer',
              '&:hover': {
                background: 'none',
                bgcolor: '#ececec',
              },
            }}
            onClick={() => navigate(routes.fulfillReport(id))}
          >
            <Grid container spacing={2} alignItems="center" wrap={!isMobile ? 'nowrap' : undefined}>
              <Grid item>
                <Typography
                  variant="h6"
                  color="primary"
                  fontWeight={500}
                  sx={{ textTransform: 'capitalize' }}
                >
                  {title} - {format(new Date(start_date), 'dd MMM yy')}
                </Typography>
                <LinearProgress variant="determinate" value={meta.progress?.percentage}/>
                <Typography variant="body1" mt={1}>
                  {
                    dictionary.reports.list.metaInfo(
                      meta.progress?.answered,
                      meta.progress?.total,
                      format(new Date(updated_at), 'dd MMM yy'),
                      `${updated_by?.first_name} ${updated_by?.last_name}`,
                    )
                  }
                </Typography>
              </Grid>
              <Box ml="auto"/>
              <Grid item xs={6} sm="auto">
                <Chip label={getReportStatusLabels(dictionary)[status as ReportStatus]} variant="outlined" color="primary"/>
              </Grid>
              {created_by && (
                <Grid item xs={6} sm="auto">
                  <Chip
                    avatar={<Avatar alt={`${created_by?.first_name} ${created_by?.last_name}`}>{created_by?.first_name.charAt(0).toUpperCase()}</Avatar>}
                    label={`${created_by?.first_name} ${created_by?.last_name}`}
                  />
                </Grid>
              )}
              <Grid item xs={6} sm="auto">
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete(id);
                  }}
                  edge="end"
                >
                  <Tooltip title={dictionary.delete}>
                    {reportBeingDeleted === id ? <CircularProgress style={{ width: 22, height: 22 }}/> : <Delete/>}
                  </Tooltip>
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      ))}
    </>
  );
};
